import { createContext, useContext, useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { fetchWebpagesPage, fetchWebpagesCount } from '@/lib/db';
import { getCache, setCache, clearCache as clearIndexedDBCache } from '@/lib/cache';
import { createSearchEngine, type SearchEngine } from '@/lib/search/search';
import { analytics } from '@/lib/analytics';
import type { Webpage } from '@/types';
import type { RealtimeChannel, RealtimePostgresChangesPayload } from '@supabase/supabase-js';

const INITIAL_PAGE_SIZE = 10;
const PAGE_SIZE = 20;
const HYDRATION_PAGE_DELAY_MS = 40;

// Synchronously get a usable session from localStorage for optimistic auth bootstrapping.
function getOptimisticSession(): Session | null {
  try {
    const storageKey = Object.keys(localStorage).find(k => k.startsWith('sb-') && k.endsWith('-auth-token'));
    if (!storageKey) return null;
    const stored = localStorage.getItem(storageKey);
    if (!stored) return null;
    const parsed = JSON.parse(stored) as Session;
    // Reject sessions that are expired or about to expire within 60s.
    if (parsed.expires_at && parsed.expires_at * 1000 <= Date.now() + 60000) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function sanitizeWebpageForRead(webpage: Webpage): Webpage {
  return { ...webpage, html_archive: null };
}

function mergeUniqueWebpages(existing: Webpage[], incoming: Webpage[]): Webpage[] {
  if (incoming.length === 0) return existing;
  const existingIds = new Set(existing.map(webpage => webpage.id));
  const newItems = incoming.filter(webpage => !existingIds.has(webpage.id));
  if (newItems.length === 0) return existing;
  return [...existing, ...newItems];
}

interface WebpagesContextValue {
  user: User | null;
  isAuthLoading: boolean;
  webpages: Webpage[];
  totalCount: number;
  isCountLoading: boolean;
  isLoading: boolean;
  isHydratingAll: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  prefetchForSearch: () => void;
  allLoaded: boolean;
  search: (query: string) => Webpage[];
  updateWebpage: (id: string, updates: Partial<Webpage>) => Promise<void>;
  deleteWebpage: (id: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const WebpagesContext = createContext<WebpagesContextValue | null>(null);

export function useWebpages() {
  const context = useContext(WebpagesContext);
  if (!context) {
    throw new Error('useWebpages must be used within WebpagesProvider');
  }
  return context;
}

export function WebpagesProvider({ children }: { children: React.ReactNode }) {
  // Auth state - initialize once from optimistic localStorage session.
  const optimisticSessionRef = useRef<Session | null>(getOptimisticSession());
  const [user, setUser] = useState<User | null>(() => optimisticSessionRef.current?.user ?? null);
  const [isAuthLoading, setIsAuthLoading] = useState(() => !optimisticSessionRef.current?.user);
  
  // Webpages state
  const [webpages, setWebpages] = useState<Webpage[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isCountLoading, setIsCountLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isHydratingAll, setIsHydratingAll] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [allLoaded, setAllLoaded] = useState(false);
  
  const channelRef = useRef<RealtimeChannel | null>(null);
  const offsetRef = useRef(0);
  const prefetchingRef = useRef(false);
  const prefetchedRef = useRef(false);
  const isLoadingMoreRef = useRef(false);
  const hasMoreRef = useRef(false);
  const totalCountRef = useRef(0);
  const activeUserIdRef = useRef<string | null>(user?.id ?? null);
  
  // Search engine instance
  const searchEngineRef = useRef<SearchEngine | null>(null);

  // Keep refs synchronized for async loaders (avoids stale closures).
  useEffect(() => {
    isLoadingMoreRef.current = isLoadingMore;
  }, [isLoadingMore]);

  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  useEffect(() => {
    totalCountRef.current = totalCount;
  }, [totalCount]);

  useEffect(() => {
    activeUserIdRef.current = user?.id ?? null;
  }, [user?.id]);

  // Auth subscription - runs once on mount, verifies session in background
  useEffect(() => {
    // Background verification - only update if different from optimistic
    supabase.auth.getSession().then(({ data: { session } }) => {
      const verifiedUser = session?.user ?? null;
      // Only update if user changed (don't trigger re-render if same)
      setUser(prev => {
        if (prev?.id === verifiedUser?.id) return prev;
        return verifiedUser;
      });
      setIsAuthLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const newUser = session?.user ?? null;
      
      setUser(prev => {
        // Clear cache on sign out
        if (!newUser && prev?.id) {
          clearIndexedDBCache(prev.id).catch(console.warn);
        }
        return newUser;
      });
      setIsAuthLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []); // Run only once on mount - no dependencies!

  // Initial load with cache-first strategy
  useEffect(() => {
    const currentUserId = user?.id ?? null;

    if (!currentUserId) {
      setWebpages([]);
      setTotalCount(0);
      setIsCountLoading(false);
      setHasMore(false);
      setAllLoaded(false);
      setIsHydratingAll(false);
      setIsLoading(false);
      offsetRef.current = 0;
      prefetchedRef.current = false;
      prefetchingRef.current = false;
      searchEngineRef.current = null;
      totalCountRef.current = 0;
      return;
    }

    let cancelled = false;

    const loadInitial = async () => {
      let hasCachedData = false;
      let cachedCount = 0;

      prefetchingRef.current = false;
      prefetchedRef.current = false;
      setIsHydratingAll(false);

      // Try loading from IndexedDB cache first.
      try {
        const cached = await getCache(currentUserId);
        if (cached && cached.webpages.length > 0) {
          const sanitizedCached = cached.webpages.map(sanitizeWebpageForRead);
          setWebpages(sanitizedCached);
          setTotalCount(cached.totalCount);
          setHasMore(sanitizedCached.length < cached.totalCount);
          setAllLoaded(sanitizedCached.length >= cached.totalCount);
          offsetRef.current = sanitizedCached.length;
          hasCachedData = true;
          cachedCount = cached.totalCount;
          prefetchedRef.current = sanitizedCached.length >= cached.totalCount;
          setIsCountLoading(false);
        }
      } catch (e) {
        console.warn('Cache unavailable:', e);
      }

      if (cancelled) return;

      if (!hasCachedData) {
        setIsLoading(true);
        setIsCountLoading(true);
      }

      // Fetch count and first page concurrently, but apply first page as soon as it returns.
      const countPromise = fetchWebpagesCount(currentUserId)
        .then((count) => {
          if (cancelled) return;
          totalCountRef.current = count;
          setTotalCount(count);
          setIsCountLoading(false);

          if (count === 0) {
            setHasMore(false);
            setAllLoaded(offsetRef.current === 0);
            prefetchedRef.current = true;
            return;
          }

          if (offsetRef.current >= count) {
            setHasMore(false);
            setAllLoaded(true);
            prefetchedRef.current = true;
            return;
          }

          if (offsetRef.current > 0) {
            setHasMore(true);
            setAllLoaded(false);
            prefetchedRef.current = false;
          }
        })
        .catch((e) => {
          console.warn('Failed to fetch count:', e);
          if (!cancelled) {
            setIsCountLoading(false);
          }
        });

      try {
        const firstPage = await fetchWebpagesPage(currentUserId, 0, INITIAL_PAGE_SIZE);
        if (cancelled) return;

        const firstPageData = firstPage.data.map(sanitizeWebpageForRead);
        const nextOffset = firstPageData.length;
        const knownCount = totalCountRef.current;
        const reachedEndByCount = knownCount > 0 && nextOffset >= knownCount;
        const nextHasMore = firstPage.hasMore && !reachedEndByCount;

        setWebpages(firstPageData);
        offsetRef.current = nextOffset;
        setHasMore(nextHasMore);
        setAllLoaded(!nextHasMore && (knownCount > 0 ? reachedEndByCount : !firstPage.hasMore));
        prefetchedRef.current = !nextHasMore;
        setIsLoading(false);

        const cacheCount = knownCount > 0 ? knownCount : Math.max(cachedCount, nextOffset);
        setCache(currentUserId, firstPageData, cacheCount).catch(console.warn);
      } catch (e) {
        console.error('Failed to fetch data:', e);
        if (!hasCachedData && !cancelled) {
          setIsLoading(false);
        }
      }

      void countPromise;
    };

    loadInitial();

    return () => { cancelled = true; };
  }, [user?.id]);

  // Build/rebuild search index when webpages change - DEFERRED to not block render
  useEffect(() => {
    const currentUserId = user?.id ?? null;
    if (!currentUserId || webpages.length === 0) {
      searchEngineRef.current = null;
      return;
    }

    const needsRebuild = !searchEngineRef.current || Math.abs((searchEngineRef.current?.getDocCount() ?? 0) - webpages.length) > 5;
    if (!needsRebuild) return;

    // Defer index building to not block initial render
    const buildIndex = () => {
      // Create or rebuild search engine
      if (!searchEngineRef.current) {
        searchEngineRef.current = createSearchEngine(currentUserId, webpages);
      } else {
        const currentCount = searchEngineRef.current.getDocCount();
        if (Math.abs(currentCount - webpages.length) > 5 || currentCount === 0) {
          searchEngineRef.current.rebuild(webpages);
        }
      }

      // Save index to storage
      searchEngineRef.current?.saveToStorage().catch(console.warn);
    };

    // Use requestIdleCallback if available, otherwise setTimeout
    if ('requestIdleCallback' in window) {
      const id = requestIdleCallback(buildIndex, { timeout: 2000 });
      return () => cancelIdleCallback(id);
    } else {
      const id = setTimeout(buildIndex, 50);
      return () => clearTimeout(id);
    }
  }, [user?.id, webpages]);

  // Load more (for infinite scroll)
  const loadMore = useCallback(async () => {
    if (!user || isLoadingMore || !hasMore || allLoaded || prefetchingRef.current) return;

    setIsLoadingMore(true);
    try {
      const result = await fetchWebpagesPage(user.id, offsetRef.current, PAGE_SIZE);
      const sanitizedData = result.data.map(sanitizeWebpageForRead);
      const nextOffset = offsetRef.current + sanitizedData.length;
      const reachedEndByCount = totalCountRef.current > 0 && nextOffset >= totalCountRef.current;
      const nextHasMore = result.hasMore && !reachedEndByCount;

      offsetRef.current = nextOffset;
      setWebpages(prev => {
        const updated = mergeUniqueWebpages(prev, sanitizedData);
        const cacheCount = totalCountRef.current > 0
          ? totalCountRef.current
          : Math.max(nextOffset, updated.length);
        setCache(user.id, updated, cacheCount).catch(console.warn);
        return updated;
      });

      setHasMore(nextHasMore);
      if (!nextHasMore) {
        setAllLoaded(true);
        prefetchedRef.current = true;
      }
    } catch (e) {
      console.error('Load more failed:', e);
    } finally {
      setIsLoadingMore(false);
    }
  }, [user, isLoadingMore, hasMore, allLoaded]);

  // Pre-fetch all for search (call when search bar is focused)
  const prefetchForSearch = useCallback(() => {
    if (!user || allLoaded || prefetchingRef.current || prefetchedRef.current) return;
    const currentUserId = user.id;

    if (!hasMoreRef.current) {
      if (totalCountRef.current > 0 && offsetRef.current >= totalCountRef.current) {
        setAllLoaded(true);
        prefetchedRef.current = true;
      }
      return;
    }

    prefetchingRef.current = true;
    setIsHydratingAll(true);

    const hydrate = async () => {
      try {
        while (hasMoreRef.current) {
          if (isLoadingMoreRef.current) {
            await new Promise(resolve => setTimeout(resolve, HYDRATION_PAGE_DELAY_MS * 2));
            continue;
          }

          if (activeUserIdRef.current !== currentUserId) {
            break;
          }

          const result = await fetchWebpagesPage(currentUserId, offsetRef.current, PAGE_SIZE);
          const sanitizedData = result.data.map(sanitizeWebpageForRead);

          if (sanitizedData.length === 0) {
            setHasMore(false);
            setAllLoaded(true);
            prefetchedRef.current = true;
            break;
          }

          const nextOffset = offsetRef.current + sanitizedData.length;
          const reachedEndByCount = totalCountRef.current > 0 && nextOffset >= totalCountRef.current;
          const nextHasMore = result.hasMore && !reachedEndByCount;
          offsetRef.current = nextOffset;

          setWebpages(prev => {
            const updated = mergeUniqueWebpages(prev, sanitizedData);
            const cacheCount = totalCountRef.current > 0
              ? totalCountRef.current
              : Math.max(nextOffset, updated.length);
            setCache(currentUserId, updated, cacheCount).catch(console.warn);
            return updated;
          });

          setHasMore(nextHasMore);
          if (!nextHasMore) {
            setAllLoaded(true);
            prefetchedRef.current = true;
            break;
          }

          await new Promise(resolve => setTimeout(resolve, HYDRATION_PAGE_DELAY_MS));
        }
      } catch (e) {
        console.warn('Prefetch for search failed:', e);
      } finally {
        prefetchingRef.current = false;
        setIsHydratingAll(false);
      }
    };

    void hydrate();
  }, [user, allLoaded]);

  // Realtime subscription
  useEffect(() => {
    const currentUserId = user?.id ?? null;
    if (!currentUserId) return;

    const channel = supabase
      .channel(`webpages_${currentUserId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'webpages',
          filter: `user_id=eq.${currentUserId}`,
        },
        (payload: RealtimePostgresChangesPayload<Webpage>) => {
          // Update search index incrementally
          const searchEngine = searchEngineRef.current;
          
          if (payload.eventType === 'INSERT') {
            const newWebpage = sanitizeWebpageForRead(payload.new as Webpage);
            setWebpages(prev => {
              const updated = [newWebpage, ...prev.filter(webpage => webpage.id !== newWebpage.id)];
              const nextCount = Math.max(totalCountRef.current + 1, updated.length);
              setCache(currentUserId, updated, nextCount).catch(console.warn);
              return updated;
            });
            setTotalCount(prev => prev + 1);
            // Add to search index
            if (searchEngine) {
              searchEngine.addWebpage(newWebpage);
            }
          } else if (payload.eventType === 'DELETE') {
            const deletedId = payload.old.id;
            setWebpages(prev => {
              const updated = prev.filter(w => w.id !== deletedId);
              const nextCount = Math.max(0, Math.max(totalCountRef.current - 1, updated.length));
              setCache(currentUserId, updated, nextCount).catch(console.warn);
              return updated;
            });
            setTotalCount(prev => Math.max(0, prev - 1));
            // Remove from search index
            if (searchEngine) {
              searchEngine.removeWebpage(deletedId);
            }
          } else if (payload.eventType === 'UPDATE') {
            const updatedWebpage = sanitizeWebpageForRead(payload.new as Webpage);
            setWebpages(prev => {
              const updated = prev.map(w => (w.id === updatedWebpage.id ? updatedWebpage : w));
              setCache(currentUserId, updated, totalCountRef.current).catch(console.warn);
              return updated;
            });
            // Update in search index
            if (searchEngine) {
              searchEngine.updateWebpage(updatedWebpage);
            }
          }
        }
      )
      .subscribe();

    channelRef.current = channel;
    return () => {
      if (channelRef.current) supabase.removeChannel(channelRef.current);
    };
  }, [user?.id]);

  // BM25-powered search with inverted index
  const search = useCallback((query: string): Webpage[] => {
    // Return all webpages if no query
    if (!query || !query.trim()) {
      return webpages;
    }

    // Use search engine if available
    const searchEngine = searchEngineRef.current;
    if (searchEngine && searchEngine.isReady()) {
      const results = searchEngine.search(query);
      return results.map(r => r.webpage);
    }

    // Fallback: simple token-based search if engine not ready
    // This ensures search works even before index is built
    const queryTokens = query.toLowerCase().split(/\s+/).filter(t => t.length > 0);
    
    return webpages.filter(webpage => {
      const text = [
        webpage.title,
        webpage.description,
        webpage.url,
        webpage.site_name,
        webpage.author,
        webpage.content,
        webpage.notes,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return queryTokens.every(token => text.includes(token));
    });
  }, [webpages]);

  const signOut = useCallback(async () => {
    // Track BEFORE reset so the event keeps the identified distinct_id.
    if (user?.id) {
      analytics.track('signout', { user_id: user.id });
      await clearIndexedDBCache(user.id).catch(console.warn);
      // Clear search index
      if (searchEngineRef.current) {
        await searchEngineRef.current.clearStorage().catch(console.warn);
        searchEngineRef.current = null;
      }
    }
    await supabase.auth.signOut();
    // Reset PostHog AFTER Supabase clears its session — next visit starts anon.
    analytics.reset();
  }, [user?.id]);

  // Update a webpage
  const updateWebpage = useCallback(async (id: string, updates: Partial<Webpage>) => {
    if (!user) throw new Error('Must be signed in to update webpages');
    
    const { error } = await supabase
      .from('webpages')
      .update(updates)
      .eq('id', id)
      .eq('user_id', user.id);
    
    if (error) {
      console.error('Error updating webpage:', error);
      throw error;
    }
    // Realtime subscription will handle the UI update
  }, [user]);

  // Delete a webpage
  const deleteWebpage = useCallback(async (id: string) => {
    if (!user) throw new Error('Must be signed in to delete webpages');
    
    const { error } = await supabase
      .from('webpages')
      .delete()
      .eq('id', id)
      .eq('user_id', user.id);
    
    if (error) {
      console.error('Error deleting webpage:', error);
      throw error;
    }
    // Realtime subscription will handle the UI update
  }, [user]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    user,
    isAuthLoading,
    webpages,
    totalCount,
    isCountLoading,
    isLoading,
    isHydratingAll,
    isLoadingMore,
    hasMore,
    loadMore,
    prefetchForSearch,
    allLoaded,
    search,
    updateWebpage,
    deleteWebpage,
    signOut,
  }), [
    user,
    isAuthLoading,
    webpages,
    totalCount,
    isCountLoading,
    isLoading,
    isHydratingAll,
    isLoadingMore,
    hasMore,
    loadMore,
    prefetchForSearch,
    allLoaded,
    search,
    updateWebpage,
    deleteWebpage,
    signOut,
  ]);

  return (
    <WebpagesContext.Provider value={contextValue}>
      {children}
    </WebpagesContext.Provider>
  );
}
